import { PollyAuthoringOrigin, TeamsSurveyTypes } from "@teams/types";
import { useCallback, useEffect } from "react";

import { useUserInfo } from "../../../../../_shared/actions/useUserInfo";
import { clientEventTracker } from "../../../../../_shared/utils/clientTelemetry";
import { useTrackingProperties } from "./useTrackingProperties";

export function useTracking(data: {
  type: TeamsSurveyTypes;
  isEdit?: boolean;
  reOpen?: boolean;
  duplicate?: boolean;
  apiOnly?: boolean;
  draftId?: string;
  templateId?: string;
  trackingSource: string;
  authoringOrigin: PollyAuthoringOrigin;
  surveyId?: string;
  skipInstrumentation: boolean;
  isPersonal: boolean;
}) {
  const {
    isEdit,
    reOpen,
    duplicate,
    apiOnly,
    trackingSource,
    authoringOrigin,
    type,
    surveyId,
    draftId,
    templateId,
    skipInstrumentation,
    isPersonal,
  } = data;
  const { user } = useUserInfo();

  const { inActiveMeeting, groupType, hostClientType, meetingType } = useTrackingProperties({
    isPersonal,
  });

  useEffect(() => {
    if (!reOpen && !isEdit && !skipInstrumentation) {
      clientEventTracker.track("polly authoring started", user, {
        pollyType: type,
        fromDraft: !!draftId,
        fromPolly: !!surveyId,
        templateId,
        apiOnly,
        trackingSource,
        inActiveMeeting,
        groupType,
        authoringOrigin,
        hostClientType,
        ...(meetingType && { meetingType }),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const trackReopenPageViewEvent = useCallback(() => {
    clientEventTracker.track("polly reopen started", user, {
      apiOnly,
      trackingSource,
      pollyType: type,
      surveyId,
    });
  }, [apiOnly, surveyId, trackingSource, type, user]);

  const trackEditPageViewEvent = useCallback(() => {
    clientEventTracker.track("polly editing started", user, {
      apiOnly,
      trackingSource,
      pollyType: type,
      surveyId,
    });
  }, [apiOnly, surveyId, trackingSource, type, user]);

  const trackDuplicatePageViewEvent = useCallback(() => {
    clientEventTracker.track("polly duplicate started", user, {
      apiOnly,
      trackingSource,
      pollyType: type,
      surveyId,
    });
  }, [apiOnly, surveyId, trackingSource, type, user]);

  useEffect(() => {
    if (!duplicate) {
      return;
    }

    trackDuplicatePageViewEvent();
  }, [duplicate, trackDuplicatePageViewEvent]);

  useEffect(() => {
    if (!isEdit || reOpen) {
      return;
    }

    trackEditPageViewEvent();
  }, [isEdit, reOpen, trackEditPageViewEvent]);

  useEffect(() => {
    if (!reOpen) {
      return;
    }

    trackReopenPageViewEvent();
  }, [reOpen, trackReopenPageViewEvent]);
}
